/**
 * Route Geometry Utilities
 *
 * @version 1.0
 * @module core/modules/waste/utils
 *
 * @description
 * Utilidades para cálculos geométricos de rutas de recolección.
 * Incluye cálculo de distancias, generación de geometrías desde contenedores,
 * y funciones de formateo.
 */

import type { LineString } from 'geojson';
import type { WasteContainer } from '../types';

/**
 * Radio de la Tierra en kilómetros
 */
const EARTH_RADIUS_KM = 6371;

/**
 * Convierte grados a radianes
 *
 * @param degrees - Ángulo en grados
 * @returns Ángulo en radianes
 */
function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Calcula la distancia entre dos coordenadas usando la fórmula de Haversine
 *
 * @param coord1 - Primera coordenada [lng, lat]
 * @param coord2 - Segunda coordenada [lng, lat]
 * @returns Distancia en kilómetros
 *
 * @remarks
 * La fórmula de Haversine calcula la distancia más corta entre dos puntos
 * en la superficie de una esfera (Earth). Es precisa para la mayoría de
 * aplicaciones de mapeo urbano.
 *
 * @example
 * ```typescript
 * const distance = calculateHaversineDistance(
 *   [-62.2663, -38.7183], // Bahía Blanca
 *   [-62.2800, -38.7300]
 * );
 * console.log(distance); // ~1.8 km
 * ```
 */
function calculateHaversineDistance(
  coord1: [number, number],
  coord2: [number, number]
): number {
  const [lng1, lat1] = coord1;
  const [lng2, lat2] = coord2;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
}

/**
 * Calcula la distancia total de un LineString
 *
 * @param geometry - Geometría LineString (ruta)
 * @returns Distancia total en kilómetros
 *
 * @remarks
 * Suma las distancias entre todos los segmentos consecutivos de la línea.
 * Retorna 0 si la geometría es inválida o tiene menos de 2 puntos.
 *
 * @example
 * ```typescript
 * const route: LineString = {
 *   type: 'LineString',
 *   coordinates: [
 *     [-62.266, -38.718],
 *     [-62.270, -38.720],
 *     [-62.274, -38.722],
 *   ]
 * };
 * const distance = calculateLineStringDistance(route);
 * console.log(formatDistance(distance)); // "0.8 km"
 * ```
 */
export function calculateLineStringDistance(geometry: LineString): number {
  if (!geometry || !geometry.coordinates || geometry.coordinates.length < 2) {
    return 0;
  }

  let totalDistance = 0;

  for (let i = 0; i < geometry.coordinates.length - 1; i++) {
    const coord1 = geometry.coordinates[i] as [number, number];
    const coord2 = geometry.coordinates[i + 1] as [number, number];
    totalDistance += calculateHaversineDistance(coord1, coord2);
  }

  return totalDistance;
}

/**
 * Formatea una distancia en kilómetros a string legible
 *
 * @param kilometers - Distancia en kilómetros
 * @returns String formateado (ej: "2.5 km" o "850 m")
 *
 * @remarks
 * - Si la distancia es >= 1 km, muestra en km con 1 decimal
 * - Si la distancia es < 1 km, muestra en metros sin decimales
 *
 * @example
 * ```typescript
 * formatDistance(2.543);  // "2.5 km"
 * formatDistance(0.850);  // "850 m"
 * formatDistance(0.023);  // "23 m"
 * ```
 */
export function formatDistance(kilometers: number): string {
  if (kilometers >= 1) {
    return `${kilometers.toFixed(1)} km`;
  }
  const meters = Math.round(kilometers * 1000);
  return `${meters} m`;
}

/**
 * Formatea una duración en minutos a string legible
 *
 * @param minutes - Duración en minutos
 * @returns String formateado (ej: "1h 30min" o "45 min")
 *
 * @remarks
 * - Si la duración es >= 60 min, muestra horas y minutos
 * - Si la duración es < 60 min, solo muestra minutos
 *
 * @example
 * ```typescript
 * formatDuration(90);   // "1h 30min"
 * formatDuration(45);   // "45 min"
 * formatDuration(125);  // "2h 5min"
 * ```
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}min`;
}

/**
 * Genera una geometría LineString conectando contenedores
 *
 * @param containers - Array de contenedores a conectar
 * @returns LineString que conecta todos los contenedores en orden
 *
 * @remarks
 * Los contenedores deben tener geometría Point válida.
 * El orden del array define el orden de conexión.
 * Retorna null si hay menos de 2 contenedores o alguno tiene geometría inválida.
 *
 * @example
 * ```typescript
 * const containers = [
 *   { id: '1', geometry: { type: 'Point', coordinates: [-62.266, -38.718] } },
 *   { id: '2', geometry: { type: 'Point', coordinates: [-62.270, -38.720] } },
 *   { id: '3', geometry: { type: 'Point', coordinates: [-62.274, -38.722] } },
 * ];
 *
 * const route = generateGeometryFromContainers(containers);
 * // LineString con 3 coordenadas
 * ```
 */
export function generateGeometryFromContainers(
  containers: WasteContainer[]
): LineString | null {
  if (!containers || containers.length < 2) {
    return null;
  }

  const coordinates: [number, number][] = [];

  for (const container of containers) {
    if (
      !container.geometry ||
      container.geometry.type !== 'Point' ||
      !Array.isArray(container.geometry.coordinates) ||
      container.geometry.coordinates.length !== 2
    ) {
      console.warn(
        `[generateGeometryFromContainers] Container ${container.id} has invalid geometry`,
        container.geometry
      );
      return null;
    }

    coordinates.push(container.geometry.coordinates as [number, number]);
  }

  return {
    type: 'LineString',
    coordinates,
  };
}

/**
 * Calcula el bounding box de una geometría LineString
 *
 * @param geometry - Geometría LineString
 * @returns Bounding box [minLng, minLat, maxLng, maxLat] o null si inválido
 *
 * @remarks
 * Útil para hacer fitBounds en el mapa y centrar la vista en la ruta.
 *
 * @example
 * ```typescript
 * const route: LineString = {
 *   type: 'LineString',
 *   coordinates: [
 *     [-62.266, -38.718],
 *     [-62.280, -38.730],
 *   ]
 * };
 *
 * const bbox = getBoundingBox(route);
 * // [-62.280, -38.730, -62.266, -38.718]
 *
 * // Usar en MapLibre:
 * map.fitBounds(bbox, { padding: 50 });
 * ```
 */
export function getBoundingBox(
  geometry: LineString
): [number, number, number, number] | null {
  if (!geometry || !geometry.coordinates || geometry.coordinates.length === 0) {
    return null;
  }

  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  for (const coord of geometry.coordinates) {
    const [lng, lat] = coord;
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  }

  return [minLng, minLat, maxLng, maxLat];
}

/**
 * Estima la duración de recorrido basada en distancia
 *
 * @param distanceKm - Distancia de la ruta en kilómetros
 * @param containersCount - Cantidad de contenedores en la ruta
 * @returns Duración estimada en minutos
 *
 * @remarks
 * Cálculo simplificado basado en:
 * - Velocidad promedio: 20 km/h (considerando tráfico urbano)
 * - Tiempo por contenedor: 3 minutos (parada + vaciado)
 *
 * Esta es una estimación básica. Para cálculos más precisos,
 * se debe considerar:
 * - Tipo de contenedor (capacidad)
 * - Tipo de vía (velocidad permitida)
 * - Restricciones de tráfico
 * - Horario del día
 *
 * @example
 * ```typescript
 * const duration = estimateRouteDuration(5.2, 15);
 * // 5.2 km / 20 km/h * 60 = 15.6 min viaje
 * // 15 contenedores * 3 min = 45 min paradas
 * // Total: ~61 min
 * console.log(formatDuration(duration)); // "1h 1min"
 * ```
 */
export function estimateRouteDuration(
  distanceKm: number,
  containersCount: number
): number {
  const AVERAGE_SPEED_KMH = 20; // Velocidad promedio urbana
  const MINUTES_PER_CONTAINER = 3; // Tiempo promedio por contenedor

  const travelTimeMinutes = (distanceKm / AVERAGE_SPEED_KMH) * 60;
  const stopTimeMinutes = containersCount * MINUTES_PER_CONTAINER;

  return Math.round(travelTimeMinutes + stopTimeMinutes);
}

/**
 * Valida si una geometría LineString es válida
 *
 * @param geometry - Geometría a validar
 * @returns true si es válida, false en caso contrario
 *
 * @remarks
 * Validaciones:
 * - Tipo debe ser 'LineString'
 * - Debe tener al menos 2 coordenadas
 * - Cada coordenada debe ser [lng, lat] válida
 * - Lng debe estar entre -180 y 180
 * - Lat debe estar entre -90 y 90
 */
export function isValidLineString(geometry: unknown): geometry is LineString {
  if (!geometry || typeof geometry !== 'object') {
    return false;
  }

  const g = geometry as Partial<LineString>;

  if (g.type !== 'LineString') {
    return false;
  }

  if (!Array.isArray(g.coordinates) || g.coordinates.length < 2) {
    return false;
  }

  for (const coord of g.coordinates) {
    if (!Array.isArray(coord) || coord.length !== 2) {
      return false;
    }

    const [lng, lat] = coord;

    if (
      typeof lng !== 'number' ||
      typeof lat !== 'number' ||
      lng < -180 ||
      lng > 180 ||
      lat < -90 ||
      lat > 90
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Optimiza el orden de contenedores usando algoritmo Nearest Neighbor (greedy)
 *
 * @param containers - Array de contenedores a optimizar
 * @param startIndex - Índice del contenedor inicial (opcional, default: 0)
 * @returns Array de contenedores en orden optimizado
 *
 * @remarks
 * Este algoritmo es una heurística greedy que no garantiza la solución óptima
 * pero es rápido y da buenos resultados en la mayoría de casos.
 *
 * Para optimización óptima con TSP, usar el endpoint backend:
 * POST /waste/routes/:id/optimize
 *
 * Complejidad: O(n²)
 *
 * @example
 * ```typescript
 * const containers = [c1, c2, c3, c4];
 * const optimized = optimizeContainerOrder(containers);
 * // Retorna contenedores en orden que minimiza distancia total
 * ```
 */
export function optimizeContainerOrder(
  containers: WasteContainer[],
  startIndex = 0
): WasteContainer[] {
  if (containers.length <= 2) {
    return containers;
  }

  const unvisited = [...containers];
  const route: WasteContainer[] = [];

  // Comenzar con el contenedor inicial
  let current = unvisited.splice(startIndex, 1)[0];
  route.push(current);

  // Mientras haya contenedores sin visitar
  while (unvisited.length > 0) {
    let nearestIndex = 0;
    let minDistance = Infinity;

    const currentCoord = current.geometry.coordinates as [number, number];

    // Encontrar el contenedor más cercano
    unvisited.forEach((container, index) => {
      const coord = container.geometry.coordinates as [number, number];
      const distance = calculateHaversineDistance(currentCoord, coord);

      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }
    });

    // Mover al siguiente contenedor más cercano
    current = unvisited.splice(nearestIndex, 1)[0];
    route.push(current);
  }

  return route;
}

/**
 * Calcula la mejora de distancia al optimizar una ruta
 *
 * @param originalContainers - Contenedores en orden original
 * @param optimizedContainers - Contenedores en orden optimizado
 * @returns Objeto con distancias y mejora
 *
 * @example
 * ```typescript
 * const improvement = calculateOptimizationImprovement(original, optimized);
 * // { originalDistance: 5.2, optimizedDistance: 4.1, improvement: 1.1, improvementPercent: 21.2 }
 * ```
 */
export function calculateOptimizationImprovement(
  originalContainers: WasteContainer[],
  optimizedContainers: WasteContainer[]
): {
  originalDistance: number;
  optimizedDistance: number;
  improvement: number;
  improvementPercent: number;
} {
  const originalGeometry = generateGeometryFromContainers(originalContainers);
  const optimizedGeometry = generateGeometryFromContainers(optimizedContainers);

  if (!originalGeometry || !optimizedGeometry) {
    return {
      originalDistance: 0,
      optimizedDistance: 0,
      improvement: 0,
      improvementPercent: 0,
    };
  }

  const originalDistance = calculateLineStringDistance(originalGeometry);
  const optimizedDistance = calculateLineStringDistance(optimizedGeometry);

  const improvement = originalDistance - optimizedDistance;
  const improvementPercent = originalDistance > 0
    ? (improvement / originalDistance) * 100
    : 0;

  return {
    originalDistance,
    optimizedDistance,
    improvement,
    improvementPercent,
  };
}
